import React, { useState, useEffect } from 'react';
import { 
  Wallet, ArrowUpRight, Clock, CheckCircle, XCircle, AlertCircle,
  Building, CreditCard, Smartphone, RefreshCw, Send, X, ChevronDown
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const WithdrawalStatus = {
  pending: { label: 'Pending', color: 'text-yellow-500', bg: 'bg-yellow-500/20', icon: Clock },
  processing: { label: 'Processing', color: 'text-blue-500', bg: 'bg-blue-500/20', icon: RefreshCw },
  approved: { label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/20', icon: CheckCircle },
  completed: { label: 'Completed', color: 'text-green-500', bg: 'bg-green-500/20', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-500', bg: 'bg-red-500/20', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-500/20', icon: XCircle }
};

const WithdrawalMethods = [
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Building },
  { id: 'upi', label: 'UPI', icon: Smartphone }
];

const CommissionWithdrawal = () => {
  const { user, refreshUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    method: 'upi',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder: '',
    upi_id: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/commission/withdrawal/my-requests');
      setRequests(res.data || []);
    } catch (error) {
      console.error('Failed to fetch withdrawal requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) < 100) {
      toast.error('Minimum withdrawal amount is ₹100');
      return;
    }

    if (parseFloat(formData.amount) > (user?.commission_wallet || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    if (formData.method === 'bank_transfer') {
      if (!formData.bank_name || !formData.account_number || !formData.ifsc_code || !formData.account_holder) {
        toast.error('Please fill all bank details');
        return;
      }
    } else if (formData.method === 'upi') {
      if (!formData.upi_id) {
        toast.error('Please enter UPI ID');
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post('/commission/withdrawal/request', formData);
      toast.success(res.data.message || 'Withdrawal request submitted!');
      setShowForm(false);
      setFormData({
        amount: '',
        method: 'upi',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        account_holder: '',
        upi_id: ''
      });
      fetchRequests();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (withdrawalId) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request?')) return;
    
    try {
      await api.delete(`/commission/withdrawal/cancel/${withdrawalId}`);
      toast.success('Withdrawal cancelled. Amount refunded.');
      fetchRequests();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasPendingRequest = requests.some(r => ['pending', 'processing'].includes(r.status));

  return (
    <div className="glass-card p-6" data-testid="commission-withdrawal">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-green-500/20">
            <ArrowUpRight className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Withdraw Commission</h3>
            <p className="text-sm text-muted-foreground">
              Available: <span className="text-green-500 font-bold">{formatCurrency(user?.commission_wallet)}</span>
            </p>
          </div>
        </div>
        
        {!showForm && !hasPendingRequest && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-medium flex items-center gap-2 transition-all"
            data-testid="new-withdrawal-btn"
          >
            <Send className="w-4 h-4" />
            New Request
          </button>
        )}
      </div>

      {/* Withdrawal Form */}
      {showForm && (
        <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="withdrawal-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">New Withdrawal Request</h4>
            <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/10 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Amount */}
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Amount (₹)</label>
              <input
                type="number"
                min="100"
                max={user?.commission_wallet || 0}
                step="1"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="Enter amount (min ₹100)"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 focus:border-primary focus:outline-none"
                data-testid="withdrawal-amount"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Min: ₹100 • Max: ₹50,000 • Available: {formatCurrency(user?.commission_wallet)}
              </p>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Payment Method</label>
              <div className="grid grid-cols-2 gap-3">
                {WithdrawalMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setFormData({...formData, method: method.id})}
                      className={`p-3 rounded-xl border transition-all flex items-center gap-3 ${
                        formData.method === method.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                      data-testid={`method-${method.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{method.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bank Transfer Details */}
            {formData.method === 'bank_transfer' && (
              <div className="space-y-3 p-4 rounded-xl bg-white/5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                      placeholder="e.g. State Bank of India"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 focus:border-primary focus:outline-none text-sm"
                      data-testid="bank-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Account Holder</label>
                    <input
                      type="text"
                      value={formData.account_holder}
                      onChange={(e) => setFormData({...formData, account_holder: e.target.value})}
                      placeholder="Name on account"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 focus:border-primary focus:outline-none text-sm"
                      data-testid="account-holder"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Account Number</label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                      placeholder="Account number"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 focus:border-primary focus:outline-none text-sm"
                      data-testid="account-number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">IFSC Code</label>
                    <input
                      type="text"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({...formData, ifsc_code: e.target.value.toUpperCase()})}
                      placeholder="e.g. SBIN0001234"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 focus:border-primary focus:outline-none text-sm uppercase"
                      data-testid="ifsc-code"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* UPI Details */}
            {formData.method === 'upi' && (
              <div className="p-4 rounded-xl bg-white/5">
                <label className="block text-xs text-muted-foreground mb-1">UPI ID</label>
                <input
                  type="text"
                  value={formData.upi_id}
                  onChange={(e) => setFormData({...formData, upi_id: e.target.value})}
                  placeholder="e.g. yourname@paytm"
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 focus:border-primary focus:outline-none"
                  data-testid="upi-id"
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="submit-withdrawal"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Request
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Pending Warning */}
      {hasPendingRequest && !showForm && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-500">Pending Request</p>
            <p className="text-sm text-muted-foreground">
              You have a pending withdrawal request. Please wait for it to be processed before submitting a new one.
            </p>
          </div>
        </div>
      )}

      {/* Request History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Request History
          </h4>
          <button
            onClick={fetchRequests}
            disabled={loading}
            className="p-2 hover:bg-white/10 rounded-lg transition-all"
            data-testid="refresh-requests"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No withdrawal requests yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => {
              const status = WithdrawalStatus[request.status] || WithdrawalStatus.pending;
              const StatusIcon = status.icon;
              
              return (
                <div 
                  key={request.id}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                  data-testid={`request-${request.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${status.bg}`}>
                        <StatusIcon className={`w-5 h-5 ${status.color}`} />
                      </div>
                      <div>
                        <p className="font-bold">{formatCurrency(request.amount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {request.method === 'bank_transfer' ? (
                            <>Bank: {request.bank_name} • {request.account_number_masked}</>
                          ) : (
                            <>UPI: {request.upi_id_masked}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(request.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Admin Note */}
                  {request.admin_note && (
                    <div className="mt-3 p-2 rounded-lg bg-white/5 text-sm">
                      <span className="text-muted-foreground">Note:</span> {request.admin_note}
                    </div>
                  )}
                  
                  {/* Transaction ID */}
                  {request.transaction_id && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Transaction ID: <span className="font-mono">{request.transaction_id}</span>
                    </div>
                  )}
                  
                  {/* Cancel Button */}
                  {request.status === 'pending' && (
                    <button
                      onClick={() => handleCancel(request.id)}
                      className="mt-3 text-sm text-red-500 hover:text-red-400 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Cancel Request
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionWithdrawal;
